import type { OutreachTemplateKey } from '@/types/lead-outreach';

export interface EmailCopyContext {
  firstName: string;
  storeName: string;
  posVendor: string | null;
  proofUrl: string | null;
  incumbentVendor: string | null;
  hasCompetitorIncumbent: boolean;
}

/**
 * Pain → value prop (source: lead-engine calibration, pain taxonomy, TPB-MESSAGING-SYSTEM).
 *
 * Track A leads with Layer 1 (phygital shopping / path to purchase).
 * Follow-up 2 may tease Layer 2 (floor intelligence / StorePulse).
 * Never lead with Layer 3 or "agentic" language in cold email.
 *
 * | Pain        | Operator problem              | TPB mechanism                         | Commercial outcome              |
 * |-------------|-------------------------------|---------------------------------------|---------------------------------|
 * | DISCOVERY   | Deli-counter; no floor browse | Lift-and-learn on the sales floor     | Bigger baskets, guided upsell   |
 * | THROUGHPUT  | Lines at one counter          | Shop on floor; fulfill at counter     | Shorter lines, more transactions|
 * | MENU        | Stale menu, stock mismatch    | Live POS-connected catalog on floor   | One source of truth on the floor|
 * | EXPERIENCE  | Dated, commoditized floor     | Purpose-built path to purchase UI     | Differentiation, retention      |
 * | LOYALTY     | Points-only, no floor signal  | Pre-checkout behavior in reporting    | Smarter re-engagement           |
 * | FULFILLMENT | Order vs in-store mismatch    | In-store browse tied to live catalog  | Smoother pickup handoff         |
 * | INSIGHT     | Register-only visibility      | StorePulse weekly floor readouts      | Merch + staffing decisions      |
 * | BRAND       | No compliant ad channel       | Brand Spotlight + inventory tie-in    | Vendor dollars with readout     |
 */

export function buildEmailSubject(templateKey: OutreachTemplateKey, storeName: string): string {
  switch (templateKey) {
    case 'A1_basket':
      return `Discovery on the floor at ${storeName}`;
    case 'A1_menu':
      return `Live catalog on the floor at ${storeName}`;
    case 'A1_throughput':
      return `Shorter lines at ${storeName}`;
    case 'A1_experience':
      return `A better path to purchase at ${storeName}`;
    case 'A1_loyalty':
      return `What regulars browse at ${storeName}`;
    case 'A1_fulfillment':
      return `Matching the floor to the journey at ${storeName}`;
    case 'A1_insight':
      return `What happens before checkout at ${storeName}`;
    case 'A2_brand':
      return `Brand revenue on ${storeName}'s sales floor`;
    case 'B1_timing':
      return `Still thinking about the floor at ${storeName}?`;
    case 'B2_one_station':
      return `Start smaller at ${storeName}`;
    case 'B3_proposal_followup':
      return `Following up on ${storeName}`;
    case 'B4_peak_day':
      return `Before the next busy day at ${storeName}`;
    case 'follow_up':
      return `The sales floor at ${storeName}`;
    case 'follow_up_2':
      return `Before the register at ${storeName}`;
    default:
      return `Quick question about ${storeName}`;
  }
}

/**
 * Outreach email bodies — composed for dispensary owners.
 *
 * Product truth (message house):
 * - Category: interactive sales-floor technology; measurable layer before the register.
 * - Layer 1: path to purchase UI, live POS-connected catalog, physical + digital merchandising.
 * - Layer 2 tease only on follow-up / warm tracks: StorePulse, readouts.
 * - Physical interaction (lift-and-learn, pick-and-place) is measurable on TPB floor displays.
 * - Customers shop and build carts on the floor; the counter fulfills those orders.
 * - Start small / focused zone language is fine; do not cite station or display counts in cold email.
 * - Cold email leads with curiosity, not a diagnosis of the reader's problems.
 * - Do not paste hosted proof brief URLs in cold email bodies.
 * - Plain language; no em dashes; no causal overclaim; prefer "display/station" over "kiosk".
 */
export function buildEmailBody(templateKey: OutreachTemplateKey, ctx: EmailCopyContext): string {
  const { firstName, storeName } = ctx;

  switch (templateKey) {
    case 'A1_basket':
      return `Hi ${firstName},

For high-consideration categories like flower and edibles, a lot of the decision happens before the register. We put that work on the sales floor: shoppers pick up a product, see the story on a connected display, and build a cart with live product information, then check out with staff when they want help.

We are The Peak Beyond. Interactive sales-floor technology.

Worth 15 minutes on whether that belongs at ${storeName}?

[Your name]`;

    case 'A1_menu':
      return `Hi ${firstName},

When shoppers browse before they buy, the floor should show them what is actually in stock. We put a live catalog on the sales floor, connected to your POS, so browsing and cart-building stay aligned with the inventory your team already trusts.

Shoppers build the order there. Your counter team fulfills it.

We are The Peak Beyond. Interactive sales-floor technology.

Worth 15 minutes on whether that belongs at ${storeName}?

[Your name]`;

    case 'A1_throughput':
      return `Hi ${firstName},

On busy days, the counter often ends up doing discovery and fulfillment at once. We move browsing, learning, and cart-building onto the sales floor with a live catalog, so the counter can focus on getting people checked out.

Same staffing. More of the visit happens before the register.

We are The Peak Beyond. Interactive sales-floor technology.

Worth 15 minutes on how that could work at ${storeName}?

[Your name]`;

    case 'A1_experience':
      return `Hi ${firstName},

A floor that feels intentional gives shoppers a reason to linger and learn. We build a path to purchase there: touch a product, see the story, add to a cart, with staff in the loop when the customer wants them. Fulfillment stays at the counter.

We are The Peak Beyond. Interactive sales-floor technology.

If refreshing the experience at ${storeName} is on your list this year, I can walk through a focused start in 15 minutes.

[Your name]`;

    case 'A1_loyalty':
      return `Hi ${firstName},

Repeat customers leave useful signals on the floor: what they compared, picked up, or spent time with before anything rings up. We help operators capture that pre-checkout behavior and connect it to the visit, so follow-up with regulars can reference what they actually browsed in store.

We are The Peak Beyond. Interactive sales-floor technology.

If that matters for ${storeName}, worth 15 minutes?

[Your name]`;

    case 'A1_fulfillment':
      return `Hi ${firstName},

Most shoppers research before they walk in. The in-store question is whether the floor picks up that journey: live catalog, product education, and cart-building on the sales floor, then a clean handoff at the counter.

We are The Peak Beyond. Interactive sales-floor technology.

Worth 15 minutes on matching the floor to that visit at ${storeName}?

[Your name]`;

    case 'A1_insight':
      return `Hi ${firstName},

The register shows what sold. Merchandising gets clearer when you can also see what shoppers touched, compared, and added to cart on the floor before checkout.

That pre-checkout path is what we measure and read out for operators week to week.

We are The Peak Beyond. Interactive sales-floor technology.

Worth 15 minutes on what that looks like for ${storeName}?

[Your name]`;

    case 'A2_brand':
      return `Hi ${firstName},

Where traditional digital advertising is limited, the sales floor is often the practical place to turn vendor co-op into a real product path. We help operators run Brand Spotlight placements tied to live inventory, with a readout at the end: one approved asset, products you actually carry.

We are The Peak Beyond. Interactive sales-floor technology.

If brand revenue on the floor is worth exploring at ${storeName}, I can walk through the workflow in 15 minutes.

[Your name]`;

    case 'B1_timing':
      return `Hi ${firstName},

We talked [month/season] about making ${storeName}'s sales floor more interactive. No worries if timing was not right.

Since then, stores on our platform have stronger documented outcomes: physical product interaction on the floor, weekly StorePulse readouts on what shoppers viewed before checkout, and Brand Spotlight placements with dollar readouts vendors will actually review.

You still do not need a full remodel. Most operators start on one part of the floor, prove the numbers, then expand.

If the floor is back on your list this quarter, happy to compare notes in 15 minutes.

[Your name]`;

    case 'B2_one_station':
      return `Hi ${firstName},

Checking back. I know a full floor rollout at ${storeName} was not the right fit when we last spoke.

What many stores do instead: start in the highest-traffic category (flower, edibles, vapes, or a brand feature). Customers shop and build carts there. At the counter, your team fulfills those orders. Lines shorten because the counter is not also the discovery lane.

If starting smaller is easier to approve than a whole-store project, I can sketch what that could look like for your busiest category.

Worth a quick call?

[Your name]`;

    case 'B3_proposal_followup':
      return `Hi ${firstName},

Following up on our conversation about floor displays, interactive product discovery, or brand screen placement at ${storeName}.

No pressure if priorities shifted. Since we last spoke, the proof side is stronger: basket patterns we can measure when stations record floor interaction, throughput gains when discovery moves off the counter, and Brand Spotlight readouts with real dollar outcomes.

If the project is on hold, tell me and I will check back later. If it is stuck on budget, timing, or where on the floor to start, we can usually scope a smaller first step.

Reply "later" or "let's talk" and I will take it from there.

[Your name]`;

    case 'B4_peak_day':
      return `Hi ${firstName},

With [4/20 / your busy season / the next rush weekend] coming up, I wanted to reach back out.

On rush days, the register only shows what sold. It does not show what shoppers were building on the floor while your team was buried fulfilling orders at the counter. Stores that move shopping to self-serve floor displays shorten that bottleneck when volume spikes.

In one operator's 4/20 week on our platform: at one store, baskets were about $19 higher when floor stations detected physical product interaction vs. when they did not. At another store the same day, shoppers who tapped a featured brand on screen spent about $6 more than shoppers who saw the screen but did not tap.

If ${storeName} is prepping for a heavy weekend, I can share how operators use floor data to staff and merchandise for the rush.

15 minutes?

[Your name]`;

    case 'follow_up':
      // Track A FU1 — value bump for a cold reader who does not know TPB.
      // Sound bites §10: #3, #5 + observational proof only (no % range in cold email).
      return `Hi ${firstName},

Shoppers decide a lot on the floor before anything rings up. We turn that part of the visit into an active discovery, merchandising, and sales environment: touch a product, see the story, build a cart with live product information, then check out with staff when they want help.

In a recent study, baskets were higher at every location when shoppers physically engaged with products before checkout.

Worth 15 minutes on whether that pattern could show up at ${storeName}?

[Your name]`;

    case 'follow_up_2':
      // Track A FU2 — Layer 2 tease in plain language + one-pager, then stop.
      return `Hi ${firstName},

The register shows what sold. The useful picture for merchandising is usually what happened before that: what shoppers touched, compared, and added to cart on the floor.

That pre-checkout path is what we measure and read out for operators. I have a one-page summary from a recent study on interaction-linked basket patterns. Reply "send it" and I will forward it.

[Your name]`;

    default:
      return '';
  }
}
